import type { ArticleCategory, Unit } from '../data/mockData'

export interface OpenFoodFactsProduct {
  code: string
  name: string
  brand?: string
  imageUrl?: string
  thumbnailUrl?: string
  quantity?: string
  categories: string[]
  rawCategories?: string
}

export type BarcodeErrorType = 'not_found' | 'offline' | 'timeout' | 'rate_limit' | 'server_error' | 'invalid_code'

export interface BarcodeLookupResult {
  product: OpenFoodFactsProduct | null
  errorType?: BarcodeErrorType
  errorMessage?: string
}

/**
 * Queries Open Food Facts API v3 for a product by barcode with detailed error classifications
 */
export async function fetchProductByBarcodeDetailed(barcode: string, timeoutMs = 7000): Promise<BarcodeLookupResult> {
  const cleanCode = barcode.trim().replace(/\s+/g, '')
  if (!cleanCode) {
    return {
      product: null,
      errorType: 'invalid_code',
      errorMessage: 'Ungültiger oder leerer Barcode.'
    }
  }

  const urls = [
    `https://de.openfoodfacts.org/api/v3/product/${encodeURIComponent(cleanCode)}.json`,
    `https://world.openfoodfacts.org/api/v3/product/${encodeURIComponent(cleanCode)}.json`
  ]

  let lastErrorType: BarcodeErrorType = 'server_error'
  let lastErrorMessage = 'Open Food Facts ist momentan nicht erreichbar.'

  for (const url of urls) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'KochbuchUndEinkaufsliste/1.0 (web-app)'
        }
      })

      if (res.status === 429 || res.status === 503) {
        lastErrorType = 'rate_limit'
        lastErrorMessage = 'Open Food Facts ist momentan stark ausgelastet (Rate-Limit / 503). Bitte versuche es in wenigen Sekunden erneut.'
        continue
      }

      if (res.status >= 500) {
        lastErrorType = 'server_error'
        lastErrorMessage = `Open Food Facts Server-Fehler (Status ${res.status}). Bitte versuche es später noch einmal.`
        continue
      }

      const contentType = res.headers.get('content-type') || ''
      if (!contentType.includes('json')) {
        lastErrorType = 'server_error'
        lastErrorMessage = 'Unerwartete Antwort von Open Food Facts (kein JSON).'
        continue
      }

      const data = await res.json()
      if (!data || data.status === 'product not found' || data.status === 'failure' || !data.product) {
        return {
          product: null,
          errorType: 'not_found',
          errorMessage: `Barcode ${cleanCode} ist noch nicht in der Open Food Facts Datenbank hinterlegt.`
        }
      }

      const p = data.product
      const name = p.product_name_de || p.product_name || p.generic_name_de || p.generic_name || ''
      if (!name) {
        return {
          product: null,
          errorType: 'not_found',
          errorMessage: `Barcode ${cleanCode} gefunden, enthält jedoch keinen Namen in Open Food Facts.`
        }
      }

      const brand = p.brands ? p.brands.split(',')[0].trim() : undefined
      const imageUrl = p.image_front_url || p.image_url || undefined
      const thumbnailUrl = p.image_front_small_url || p.image_front_url || p.image_url || undefined
      const quantity = p.quantity || undefined

      const categories: string[] = []
      if (Array.isArray(p.categories_tags)) {
        p.categories_tags.forEach((tag: string) => {
          const cleanTag = tag.replace(/^[a-z]{2}:/, '').replace(/-/g, ' ').trim()
          if (cleanTag && !categories.includes(cleanTag)) categories.push(cleanTag)
        })
      }

      return {
        product: {
          code: cleanCode,
          name,
          brand,
          imageUrl,
          thumbnailUrl,
          quantity,
          categories,
          rawCategories: Array.isArray(p.categories_tags) ? p.categories_tags.join(', ') : undefined
        }
      }
    } catch (e: any) {
      if (e.name === 'AbortError') {
        lastErrorType = 'timeout'
        lastErrorMessage = 'Zeitüberschreitung bei der Verbindung zu Open Food Facts.'
      } else {
        lastErrorType = 'offline'
        lastErrorMessage = 'Keine Verbindung zu Open Food Facts möglich (Offline/Netzwerkfehler).'
      }
    } finally {
      clearTimeout(timer)
    }
  }

  return {
    product: null,
    errorType: lastErrorType,
    errorMessage: lastErrorMessage
  }
}

/**
 * Convenience wrapper returning null on any error
 */
export async function fetchProductByBarcode(barcode: string, timeoutMs = 7000): Promise<OpenFoodFactsProduct | null> {
  const result = await fetchProductByBarcodeDetailed(barcode, timeoutMs)
  return result.product
}

/**
 * Intelligent category matching between Open Food Facts categories and user's ArticleCategory list
 */
export function matchBestCategory(
  productCategories: string[],
  existingCategories: ArticleCategory[]
): string | undefined {
  if (!productCategories || productCategories.length === 0 || !existingCategories || existingCategories.length === 0) {
    return undefined
  }

  // Strip language prefixes like "de:", "en:", replace hyphens
  const cleanTags = productCategories.map(c => 
    c.toLowerCase().replace(/^[a-z]{2}:/, '').replace(/-/g, ' ').trim()
  )

  // Category keywords & synonyms mapping
  const categoryKeywords: Record<string, string[]> = {
    'obst': ['obst', 'frucht', 'früchte', 'fruits', 'fruit', 'beeren', 'äpfel', 'aepfel', 'bananen', 'zitrus'],
    'gemüse': ['gemüse', 'vegetable', 'vegetables', 'salat', 'tomaten', 'kartoffeln', 'zwiebeln', 'pilze', 'kräuter', 'hülse'],
    'milch': ['milch', 'käse', 'joghurt', 'quark', 'butter', 'sahne', 'dairy', 'dairies', 'milk', 'cheese', 'molkerei', 'schmand', 'dessert'],
    'fleisch': ['fleisch', 'wurst', 'meat', 'meats', 'schinken', 'geflügel', 'poultry', 'hähnchen', 'rind', 'beef', 'schwein', 'pork', 'hack'],
    'fisch': ['fisch', 'fish', 'meeresfrüchte', 'seafood', 'lachs', 'salmon', 'thunfisch', 'tuna', 'garnelen', 'shrimp'],
    'brot': ['brot', 'bread', 'breads', 'brötchen', 'backwaren', 'bakery', 'toast', 'baguette', 'bäckerei'],
    'nudeln': ['nudeln', 'pasta', 'pastas', 'spaghetti', 'penne', 'teigwaren', 'noodles'],
    'reis': ['reis', 'rice', 'getreide', 'cereals', 'grain', 'hafer', 'oats', 'quinoa', 'couscous', 'bulgur'],
    'backen': ['backzutaten', 'baking', 'mehl', 'flour', 'zucker', 'sugar', 'hefe', 'yeast', 'vanille', 'backpulver', 'kakao'],
    'gewürze': ['gewürze', 'spices', 'condiments', 'salz', 'salt', 'pfeffer', 'pepper', 'öl', 'oil', 'essig', 'vinegar', 'saucen', 'sauces', 'senf', 'mustard', 'ketchup', 'dressings'],
    'süß': ['süßwaren', 'suesswaren', 'sweet', 'sweets', 'schokolade', 'chocolate', 'kekse', 'cookies', 'biscuits', 'snack', 'snacks', 'chips', 'gummibärchen', 'bonbons', 'riegel'],
    'getränke': ['getränke', 'getraenke', 'beverage', 'beverages', 'drinks', 'saft', 'juice', 'wasser', 'water', 'limonade', 'tee', 'tea', 'kaffee', 'coffee', 'bier', 'beer', 'wein', 'wine', 'softdrinks'],
    'tiefkühl': ['tiefkühl', 'tiefkuehl', 'tk', 'frozen', 'eis', 'ice cream', 'pizza'],
    'konserven': ['konserven', 'canned', 'dose', 'glas', 'eingelegt', 'preserves']
  }

  // 1. Direct name match (exact or substring)
  for (const existing of existingCategories) {
    const existingLower = existing.name.toLowerCase()
    for (const tag of cleanTags) {
      if (tag.includes(existingLower) || existingLower.includes(tag)) {
        return existing.id
      }
    }
  }

  // 2. Keyword/Synonym match
  for (const existing of existingCategories) {
    const existingLower = existing.name.toLowerCase()
    for (const [key, keywords] of Object.entries(categoryKeywords)) {
      if (existingLower.includes(key)) {
        for (const tag of cleanTags) {
          if (keywords.some(kw => tag.includes(kw) || kw.includes(tag))) {
            return existing.id
          }
        }
      }
    }
  }

  return undefined
}

/**
 * Extracts unit from a product quantity string (e.g. "500 g", "1,5 l", "6 Stück")
 */
export function extractUnit(quantityStr?: string, availableUnits?: Unit[]): string | undefined {
  if (!quantityStr) return undefined
  const clean = quantityStr.trim().toLowerCase()

  const unitList = availableUnits && availableUnits.length > 0 
    ? availableUnits.map(u => u.name)
    : ['g', 'kg', 'ml', 'l', 'Stk.', 'Packung', 'Dose', 'Glas', 'Becher', 'Flasche', 'Bund', 'Zehe', 'Prise', 'TL', 'EL']

  // Patterns
  if (/\b(kg|kilogramm)\b/i.test(clean)) return findMatchingUnit('kg', unitList)
  if (/\b(g|gramm)\b/i.test(clean)) return findMatchingUnit('g', unitList)
  if (/\b(ml|milliliter)\b/i.test(clean)) return findMatchingUnit('ml', unitList)
  if (/\b(l|liter)\b/i.test(clean)) return findMatchingUnit('l', unitList)
  if (/\b(stk|stück|stueck|pieces)\b/i.test(clean)) return findMatchingUnit('Stk.', unitList) || findMatchingUnit('Stück', unitList)
  if (/\b(pkg|packung|pck|beutel)\b/i.test(clean)) return findMatchingUnit('Packung', unitList) || findMatchingUnit('Pkg.', unitList)
  if (/\b(dose|dosen|can|cans)\b/i.test(clean)) return findMatchingUnit('Dose', unitList)
  if (/\b(glas|gläser|glaeser|jar)\b/i.test(clean)) return findMatchingUnit('Glas', unitList)
  if (/\b(flasche|flaschen|fl|bottle)\b/i.test(clean)) return findMatchingUnit('Flasche', unitList)
  if (/\b(becher|cup)\b/i.test(clean)) return findMatchingUnit('Becher', unitList)

  return undefined
}

function findMatchingUnit(target: string, available: string[]): string | undefined {
  const targetLower = target.toLowerCase()
  return available.find(u => u.toLowerCase() === targetLower)
}

/**
 * Searches Open Food Facts by product name keyword and returns the first product with a valid image
 */
export async function fetchProductByName(nameQuery: string, timeoutMs = 6000): Promise<OpenFoodFactsProduct | null> {
  const clean = nameQuery.trim()
  if (!clean || clean.length < 2) return null

  const urls = [
    `https://de.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(clean)}&search_simple=1&action=process&json=1&page_size=12&fields=code,product_name,product_name_de,generic_name_de,generic_name,brands,image_front_url,image_front_small_url,image_url,categories_tags,quantity`,
    `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(clean)}&search_simple=1&action=process&json=1&page_size=12&fields=code,product_name,product_name_de,generic_name_de,generic_name,brands,image_front_url,image_front_small_url,image_url,categories_tags,quantity`
  ]

  const cleanLower = clean.toLowerCase()

  for (const url of urls) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'KochbuchUndEinkaufsliste/1.0 (web-app)'
        }
      })

      if (!res.ok) continue
      const contentType = res.headers.get('content-type') || ''
      if (!contentType.includes('json')) continue

      const data = await res.json()
      if (!data || !Array.isArray(data.products) || data.products.length === 0) continue

      // Find products that actually have images and match the search term in title/brand/category
      const withImages = data.products.filter((p: any) => p.image_front_url || p.image_url || p.image_front_small_url)
      if (withImages.length === 0) continue

      const relevant = withImages.find((p: any) => {
        const pName = (p.product_name_de || p.product_name || p.generic_name_de || p.generic_name || '').toLowerCase()
        const pBrand = (p.brands || '').toLowerCase()
        return pName.includes(cleanLower) || cleanLower.includes(pName) || pBrand.includes(cleanLower)
      }) || withImages[0]

      const name = relevant.product_name_de || relevant.product_name || relevant.generic_name_de || relevant.generic_name || clean
      const imageUrl = relevant.image_front_url || relevant.image_url
      const thumbnailUrl = relevant.image_front_small_url || imageUrl
      const quantity = relevant.quantity

      const categories: string[] = []
      if (Array.isArray(relevant.categories_tags)) {
        relevant.categories_tags.forEach((tag: string) => {
          const cleanTag = tag.replace(/^[a-z]{2}:/, '').replace(/-/g, ' ').trim()
          if (cleanTag && !categories.includes(cleanTag)) categories.push(cleanTag)
        })
      }

      return {
        code: relevant.code || '',
        name,
        brand: relevant.brands ? relevant.brands.split(',')[0].trim() : undefined,
        imageUrl,
        thumbnailUrl,
        quantity,
        categories
      }
    } catch {
      // Try next endpoint
    } finally {
      clearTimeout(timer)
    }
  }

  return null
}
